import React, { useCallback, useContext, useRef, useState } from 'react';
import t from 'react-translate';
import { css } from '@emotion/react';
import { config } from '@config/pendo.config.json';
import { AngularContext } from 'react-app';
import { threeQuartersSpacing } from 'styles/global_defaults/scaffolding';
import PillButton from 'athena/components/pill-button';
import Guides from './guides';
import PreAssignedActionItems from './pre-assigned-action-items';

export enum GuidanceSubTab {
  GUIDES = 'guides',
  ACTION_ITEMS = 'action-items',
}

type GuidanceProps = {
  onScroll: React.ComponentProps<'div'>['onScroll'];
  scrollEnabled: boolean;
};

const subTabsStyles = css`
  gap: ${threeQuartersSpacing}px;
  height: 45px;
`;

const Guidance = ({ onScroll, scrollEnabled }: GuidanceProps) => {
  const { injectServices } = useContext(AngularContext);
  const [$state, ConfirmationOverlays] = injectServices(['$state', 'ConfirmationOverlays']);
  const [activeSubTab, setActiveSubTab] = useState<GuidanceSubTab>(
    () => ($state.params.tab === GuidanceSubTab.ACTION_ITEMS ? GuidanceSubTab.ACTION_ITEMS : GuidanceSubTab.GUIDES),
  );
  const hasUnsavedActionItemChangesRef = useRef(false);
  const translationBase = t.MENTORING_PROGRAMS.GUIDANCE.SUB_TABS;
  const dataQATag = config.pendo.athena.mentorshipProgram.guidanceSubTabs;

  const subTabs: { id: GuidanceSubTab; label: string; dataQa: string }[] = [
    { id: GuidanceSubTab.GUIDES, label: translationBase.GUIDES(), dataQa: dataQATag.guides },
    { id: GuidanceSubTab.ACTION_ITEMS, label: translationBase.ACTION_ITEMS(), dataQa: dataQATag.actionItems },
  ];

  const handleSubTabClick = useCallback((id: GuidanceSubTab) => {
    if (id === activeSubTab || !hasUnsavedActionItemChangesRef.current) {
      setActiveSubTab(id);
    } else {
      ConfirmationOverlays.openConfirmationModal(
        'athena/templates/action-items-navigate-away.html',
      ).result.then(() => {
        hasUnsavedActionItemChangesRef.current = false;
        setActiveSubTab(id);
      });
    }
  }, [ConfirmationOverlays, activeSubTab]);

  const handleActionItemsUnsavedChanges = useCallback((hasChanges: boolean) => {
    hasUnsavedActionItemChangesRef.current = hasChanges;
  }, []);

  const renderSubTabs = () => (
    <div css={subTabsStyles} className='d-flex align-items-center'>
      {subTabs.map(({ id, label, dataQa }) => (
        <PillButton
          key={id}
          text={label}
          selected={activeSubTab === id}
          onClick={() => handleSubTabClick(id)}
          dataQa={dataQa}
        />
      ))}
    </div>
  );

  return (
    <div className='d-flex flex-column h-100'>
      {activeSubTab === GuidanceSubTab.GUIDES && (
        <Guides onScroll={onScroll} scrollEnabled={scrollEnabled}>
          {renderSubTabs()}
        </Guides>
      )}
      {activeSubTab === GuidanceSubTab.ACTION_ITEMS && (
        <div className='d-flex flex-column h-100'>
          <div className='d-flex align-items-center mb-4'>
            {renderSubTabs()}
          </div>
          <PreAssignedActionItems
            onScroll={onScroll}
            scrollEnabled={scrollEnabled}
            onHasUnsavedChanges={handleActionItemsUnsavedChanges}
          />
        </div>
      )}
    </div>
  );
};

export default Guidance;
